import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, User, Trophy, MessageSquare, UserPlus, Bell, BellOff, UserCheck, Clock, Users, Shield, UserMinus, X
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { profileAPI } from '../services';
import { useProfileImage, resolveImageUrl } from '../hooks/useProfileImage';
import { btn } from '../../utils/buttonStyles';
import { teammatesApi } from '../../teammates/services/teammatesApi';
import PlayerStatistics from '../components/PlayerStatistics';

export default function PlayerViewPage({ userId: propUserId, embedded = false, previewMode = false }) {
  const { handle: paramHandle } = useParams();
  // handleOrId: could be a numeric propUserId (embedded) or a handle string from the URL
  const handleOrId = propUserId || paramHandle;
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { user: currentUser } = useAuth();
  const showHeader = !embedded && location.state?.fromProfile === true;

  const [player, setPlayer] = useState(null);
  // resolvedUserId: always the numeric DB id — set after the first fetch
  const [resolvedUserId, setResolvedUserId] = useState(propUserId || null);
  const { imageUrl: profileImageUrl } = useProfileImage(player?.id, player?.profileImage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [activeSports, setActiveSports] = useState([]);
  const [teammateStatus, setTeammateStatus] = useState('none'); // 'none'|'pending_sent'|'pending_received'|'teammate'|'self'
  const [requestId, setRequestId] = useState(null);
  const [confirmCancelRequest, setConfirmCancelRequest] = useState(false);
  const [managerInfo, setManagerInfo] = useState(null);
  const [showManagedConsentModal, setShowManagedConsentModal] = useState(false);

  // Reset all state immediately when the player ID changes so stale data
  // from the previous player never shows during the new fetch
  useEffect(() => {
    setPlayer(null);
    setResolvedUserId(propUserId || null);
    setManagerInfo(null);
    setIsFollowing(false);
    setTeammateStatus('none');
    setRequestId(null);
    setActiveSports([]);
    setLoading(true);
  }, [handleOrId]);

  useEffect(() => {
    fetchPlayer();
  }, [handleOrId, currentUser?.id]);

  const fetchPlayer = async () => {
    try {
      setLoading(true);
      setError(null);

      // First call uses handleOrId (handle string or numeric id)
      // After this, playerData.id is always the numeric DB id
      const result = await profileAPI.getUserById(handleOrId);
      const playerData = result?.data || result;
      setPlayer(playerData);
      setResolvedUserId(playerData?.id || null);

      // Now determine if this is another user using the resolved numeric id
      const isOtherUser = currentUser?.id && playerData?.id !== currentUser.id;

      if (isOtherUser) {
        const statusResult = await teammatesApi.getStatus(playerData.id).catch((e) => {
          console.error('[PlayerViewPage] getStatus failed:', e);
          return null;
        });
        if (statusResult) {
          setTeammateStatus(statusResult.status || 'none');
          setRequestId(statusResult.requestId || null);
        }
      }

      // Fetch manager info if this is a managed player
      if (playerData?.managedBy) {
        try {
          const managerResult = await profileAPI.getUserById(playerData.managedBy);
          const managerData = managerResult?.data || managerResult;
          const mFirst = managerData?.firstName || managerData?.first_name || '';
          const mLast = managerData?.lastName || managerData?.last_name || '';
          setManagerInfo({ id: playerData.managedBy, userHandle: managerData?.userHandle, name: `${mFirst} ${mLast}`.trim() });
        } catch {
          // manager fetch failure is non-critical
        }
      }

    } catch (error) {
      console.error('Error fetching player:', error);
      setError('Failed to load player profile');
      toast.error('Failed to load player');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeammateClick = () => {
    if (player?.managedBy) {
      setShowManagedConsentModal(true);
    } else {
      handleAddTeammate();
    }
  };

  const handleAddTeammate = async () => {
    try {
      const result = await teammatesApi.sendRequest(player.id);
      if (result?.conflict) {
        // Already sent — re-fetch status to get the existing requestId
        const { status, requestId: rid } = await teammatesApi.getStatus(player.id);
        setTeammateStatus(status || 'pending_sent');
        setRequestId(rid || null);
      } else {
        setTeammateStatus('pending_sent');
        setRequestId(result?.id || null);
        toast.success('Teammate request sent!');
      }
    } catch (err) {
      console.error('Send request failed:', err);
      toast.error(err.message || 'Failed to send request. Please try again.');
    }
  };

  const handleCancelRequest = async () => {
    if (!confirmCancelRequest) {
      setConfirmCancelRequest(true);
      return;
    }
    try {
      await teammatesApi.cancelRequest(requestId);
      setTeammateStatus('none');
      setRequestId(null);
      setConfirmCancelRequest(false);
      toast.success('Request cancelled');
    } catch (err) {
      console.error('Cancel request failed:', err);
      setConfirmCancelRequest(false);
      toast.error(err.message || 'Failed to cancel request.');
    }
  };

  const handleAcceptRequest = async () => {
    try {
      await teammatesApi.acceptRequest(requestId);
      setTeammateStatus('teammate');
      setRequestId(null);
      toast.success('You are now teammates!');
    } catch (err) {
      console.error('Accept request failed:', err);
      toast.error(err.message || 'Failed to accept request.');
    }
  };

  const handleDeclineRequest = async () => {
    try {
      await teammatesApi.declineRequest(requestId);
      setTeammateStatus('none');
      setRequestId(null);
      toast.success('Request declined');
    } catch (err) {
      console.error('Decline request failed:', err);
      toast.error(err.message || 'Failed to decline request.');
    }
  };

  const handleRemoveTeammate = async () => {
    try {
      await teammatesApi.removeTeammate(player.id);
      setTeammateStatus('none');
      toast.success('Teammate removed');
    } catch (err) {
      console.error('Remove teammate failed:', err);
      toast.error(err.message || 'Failed to remove teammate.');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleMessagePlayer = () => {
    navigate(`/messages?with=${player.id}`);
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className={`min-h-screen ${theme.background} py-6 px-4`}>
        <div className="max-w-6xl mx-auto text-center py-12">
          <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className={`text-xl font-semibold ${theme.text} mb-2`}>Player not found</h2>
          <p className={`${theme.textSecondary} mb-6`}>{error || 'The player you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/')}
            className={`${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const firstName = player.first_name || player.firstName || '';
  const lastName = player.last_name || player.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim() || 'Unknown Player';
  const profileImage = profileImageUrl || resolveImageUrl(player.profile_image || player.profileImage);

  return (
    <div className={`${embedded ? '' : 'min-h-screen py-6 px-4 sm:px-6 lg:px-8'} ${embedded ? '' : theme.background}`}>
      <div className={`${embedded ? '' : 'max-w-7xl mx-auto'}`}>
        {/* Header - Only show when navigating from profile page */}
        {showHeader && (
          <div className="flex items-center mb-8">
            <button
              onClick={handleBack}
              className={`mr-4 p-2 ${theme.cardHover} rounded-lg transition-colors`}
            >
              <ArrowLeft className={`w-6 h-6 ${theme.textSecondary}`} />
            </button>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Player Profile</h1>
          </div>
        )}

        {/* Profile Card */}
        <div className={`${theme.card} rounded-xl ${theme.shadow} p-6 ${embedded ? 'mb-6' : 'mb-8'}`}>
          <div className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:gap-x-6">
            {/* Profile Image */}
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-4xl font-bold overflow-hidden">
              {profileImage ? (
                <img src={profileImage} alt={fullName} className="w-full h-full object-cover" />
              ) : (
                <span>{firstName.charAt(0)?.toUpperCase() || 'U'}</span>
              )}
            </div>

            {/* Profile Info */}
            <div className="flex-1 text-center md:text-left">
              <h2 className={`text-3xl font-bold ${theme.text} mb-1`}>{fullName}</h2>

              {/* Managed by badge */}
              {managerInfo && (
                <div className="flex items-center justify-center md:justify-start gap-1.5 mb-2">
                  <Shield className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                  <span className={`text-sm ${theme.textMuted}`}>Managed by</span>
                  <button
                    onClick={() => navigate(`/player/${managerInfo.userHandle || managerInfo.id}`)}
                    className="text-sm text-blue-500 hover:underline font-medium"
                  >
                    {managerInfo.name}
                  </button>
                </div>
              )}

              {activeSports.length > 0 && (
                <p className={`text-sm ${theme.textMuted} mb-4`}>
                  Plays: {activeSports.map(s => s.sportName).join(' · ')}
                </p>
              )}

              {/* Actions */}
              <div className="flex items-center justify-center md:justify-start gap-2 flex-wrap">
                {/* Teammate count — informational */}
                {player?.teammateCount > 0 && (
                  <div className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium ${theme.backgroundSecondary} ${theme.textSecondary}`}>
                    <Users className="w-4 h-4" />
                    <span>{player.teammateCount} {player.teammateCount === 1 ? 'Teammate' : 'Teammates'}</span>
                  </div>
                )}

                {/* Message — only for teammates, hidden for managed players and own profile */}
                {player?.managedBy == null && currentUser?.id && player?.id !== currentUser.id && teammateStatus === 'teammate' && (
                  <button
                    onClick={previewMode ? undefined : handleMessagePlayer}
                    disabled={previewMode}
                    className={`flex items-center gap-2 ${btn.base} ${btn.md} ${theme.buttonPrimary} ${previewMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>Message</span>
                  </button>
                )}

                {/* Follow */}
                <button
                  onClick={previewMode ? undefined : () => setIsFollowing(f => !f)}
                  disabled={previewMode}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium border
                    ${previewMode
                      ? `${theme.border} ${theme.textMuted} opacity-60 cursor-not-allowed`
                      : isFollowing
                        ? `border-blue-500 text-blue-500 hover:border-red-400 hover:text-red-400`
                        : `${theme.border} ${theme.text} ${theme.cardHover}`}`}
                >
                  {isFollowing ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                  <span>{isFollowing ? 'Unfollow' : 'Follow'}</span>
                </button>

                {/* Teammate actions — for all other players including managed players */}
                {currentUser?.id && player?.id !== currentUser.id && (
                  <>
                    {teammateStatus === 'teammate' && (
                      <>
                        <div className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-green-500 text-green-600">
                          <UserCheck className="w-4 h-4" />
                          <span>Teammates</span>
                        </div>
                        <button
                          onClick={previewMode ? undefined : handleRemoveTeammate}
                          disabled={previewMode}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium border
                            ${previewMode
                              ? `border-red-300 text-red-300 opacity-60 cursor-not-allowed`
                              : `border-red-400 text-red-500 hover:bg-red-50`}`}
                        >
                          <UserMinus className="w-4 h-4" />
                          <span>Remove</span>
                        </button>
                      </>
                    )}

                    {teammateStatus === 'pending_sent' && (
                      <button
                        onClick={previewMode ? undefined : handleCancelRequest}
                        onBlur={() => setConfirmCancelRequest(false)}
                        disabled={previewMode}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium border
                          ${previewMode
                            ? `border-yellow-300 text-yellow-300 opacity-60 cursor-not-allowed`
                            : confirmCancelRequest
                              ? `border-red-400 text-red-500 hover:bg-red-50`
                              : `border-yellow-400 text-yellow-600 hover:border-red-400 hover:text-red-500`}`}
                      >
                        {confirmCancelRequest ? <X className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                        <span>{confirmCancelRequest ? 'Cancel Request?' : 'Request Sent −'}</span>
                      </button>
                    )}

                    {teammateStatus === 'pending_received' && (
                      <>
                        <button
                          onClick={previewMode ? undefined : handleAcceptRequest}
                          disabled={previewMode}
                          className={`flex items-center gap-2 ${btn.base} ${btn.md} ${theme.buttonPrimary} ${previewMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                        >
                          <UserCheck className="w-4 h-4" />
                          <span>Accept</span>
                        </button>
                        <button
                          onClick={previewMode ? undefined : handleDeclineRequest}
                          disabled={previewMode}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium border
                            ${previewMode
                              ? `${theme.border} ${theme.textMuted} opacity-60 cursor-not-allowed`
                              : `border-red-400 text-red-500 hover:bg-red-50`}`}
                        >
                          <X className="w-4 h-4" />
                          <span>Decline</span>
                        </button>
                      </>
                    )}

                    {teammateStatus === 'none' && (
                      <button
                        onClick={previewMode ? undefined : handleAddTeammateClick}
                        disabled={previewMode}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium border
                          ${previewMode
                            ? `${theme.border} ${theme.textMuted} opacity-60 cursor-not-allowed`
                            : `${theme.border} ${theme.text} ${theme.cardHover}`}`}
                      >
                        <UserPlus className="w-4 h-4" />
                        <span>+ Add as Teammate</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Section - full page only */}
        {!embedded && (
          <div>
            <h3 className={`text-xl font-bold ${theme.text} mb-4 flex items-center`}>
              <Trophy className="w-5 h-5 mr-2 text-blue-500" />
              Statistics
            </h3>
            <PlayerStatistics userId={resolvedUserId} onSportsLoaded={setActiveSports} />
          </div>
        )}
      </div>

      {/* Managed Player Consent Modal */}
      {showManagedConsentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className={`${theme.card} rounded-2xl p-6 max-w-sm w-full ${theme.shadow}`}>
            <div className="flex flex-col items-center text-center mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                <Shield className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className={`text-lg font-bold ${theme.text} mb-2`}>Managed Player</h3>
              <p className={`text-sm ${theme.textSecondary}`}>
                This player is managed by{' '}
                {managerInfo ? (
                  <span className="font-semibold text-blue-500">{managerInfo.name}</span>
                ) : (
                  'their manager'
                )}
                . Your request will be sent to them for approval. Once approved, you can add this player to your teams and tournaments.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowManagedConsentModal(false)}
                className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonGhost}`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowManagedConsentModal(false);
                  handleAddTeammate();
                }}
                className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
              >
                Send Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
